/**
 * Work Packet view — integrated intake (from V2/V4 terminal widget lineage).
 *
 * Replaces the floating terminal widget with a first-class portal view.
 * User types intent → gets a scoped work packet with tier inference,
 * deliverables, gates, and session staging.
 *
 * This is the F0/F5 surface — the sales message rendered as an interactive
 * intake within the portal itself.
 */

import { useState, useCallback } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";

interface Tier {
  id: string;
  label: string;
  price: string;
  cta: string;
  gate: string;
}

interface Intent {
  title: string;
  email_hint: string;
  status: string;
  recommended_tier: Tier;
  tiers: Tier[];
  deliverables: string[];
  gates: string[];
  next_actions: string[];
}

interface Session {
  token: string;
  sid: string;
  expires_at: string;
  intent: Intent;
}

// Tier inference (from V4 terminalWidget)
const FALLBACK_TIERS: Tier[] = [
  { id: "gtm", label: "GTM Agent Install", price: "starts at $10K", cta: "Purchase GTM scope", gate: "scope + payment" },
  { id: "starter", label: "GetRida Starter", price: "$97/mo", cta: "Start with Starter", gate: "subscription active" },
  { id: "growth", label: "GetRida Growth", price: "$297/mo", cta: "Upgrade to Growth", gate: "subscription active" },
  { id: "scale", label: "GetRida Scale", price: "$997/mo", cta: "Scale your operations", gate: "subscription active" },
  { id: "board", label: "Board Relationship", price: "by approval", cta: "Book board fit call", gate: "approval only" },
];

function inferTier(prompt: string, requested: string): Tier {
  if (requested && requested !== "auto") {
    const explicit = FALLBACK_TIERS.find((t) => t.id === requested);
    if (explicit) return explicit;
  }
  const text = prompt.toLowerCase();
  if (/\b(board|counsel|legal|audit|governance|cfo|finance)\b/.test(text)) return FALLBACK_TIERS[4];
  if (/\b(scale|enterprise|team|multiple agents|high volume)\b/.test(text)) return FALLBACK_TIERS[3];
  if (/\b(growth|campaign|outreach|sequence|multiple)\b/.test(text)) return FALLBACK_TIERS[2];
  if (/\b(starter|basic|simple|one agent|just)\b/.test(text)) return FALLBACK_TIERS[1];
  return FALLBACK_TIERS[0];
}

function buildDeliverables(prompt: string): string[] {
  const text = prompt.toLowerCase();
  const out: string[] = [];
  if (/\b(lead|contact|prospect|campaign|email|outreach|linkedin)\b/.test(text)) {
    out.push("Candidate graph: public/permissioned people, companies, roles, and source receipts.");
    out.push("Campaign projection: segments, offer angle, volume plan, and blocked-send ledger.");
  }
  if (/\b(automate|automation|workflow|agent|inbox|crm|ops|assistant)\b/.test(text)) {
    out.push("Workflow map: intake, agent role, approval points, handoff, and receipt trail.");
    out.push("Automation build sheet: APIs/tools, auth scopes, safe defaults, and acceptance tests.");
  }
  if (/\b(offer|page|landing|website|deploy)\b/.test(text)) {
    out.push("Offer build: visual page editor, D1-backed, live deploy to your domain.");
  }
  if (!out.length) {
    out.push("Scoped work packet: goal, buyer, constraints, deliverables, price lane, and proof required.");
    out.push("Implementation plan: what can run immediately, what requires auth/payment, and what is blocked.");
  }
  return out.slice(0, 6);
}

function buildGates(prompt: string, tier: Tier): string[] {
  const gates = [
    "Payment/auth gate before execution.",
    "No external email, payment, provider change, or data pull starts from this prompt.",
  ];
  const text = prompt.toLowerCase();
  if (/\b(email|outreach|send|blast|campaign|contact)\b/.test(text)) {
    gates.push("Outreach/contact work requires consent/source review, suppression, and send approval.");
  }
  return gates;
}

export function WorkPacketView({ isOperator }: { isOperator: boolean }) {
  const [prompt, setPrompt] = useState("");
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("auto");
  const [session, setSession] = useState<Session | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [buildData, setBuildData] = useState<any>(null);
  const [buildPolling, setBuildPolling] = useState(false);

  const membraneBypass = isOperator;

  const createSession = useCallback(async () => {
    setError(null);
    setNotice(null);
    const p = prompt.trim();
    const e = email.trim();

    if (p.length < 12) { setError("Give the agent a real request first."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setError("Use a valid work email."); return; }

    setBusy(true);

    try {
      // Try the portal API first (control server)
      const clients = getClients();
      let data: any = null;

      if (clients) {
        try {
          const res = await fetch("/api/work-packet", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ prompt: p, email: e, tier }),
          });
          if (res.ok) data = await res.json();
        } catch {}
      }

      // Fallback: build locally (V4 terminalWidget pattern)
      if (!data || !data.ok) {
        const inferredTier = inferTier(p, tier);
        const emailHint = e.split("@")[0].slice(0, 2) + "***@" + e.split("@")[1];
        data = {
          ok: true,
          session: {
            sid: "local_" + Date.now().toString(36),
            email_hint: emailHint,
            status: "purchase_required",
            prompt_preview: p.length > 220 ? p.slice(0, 217) + "..." : p,
          },
          intent: {
            title: p.length > 120 ? p.slice(0, 117) + "..." : p,
            email_hint: emailHint,
            status: "purchase_required",
            recommended_tier: inferredTier,
            tiers: FALLBACK_TIERS,
            deliverables: buildDeliverables(p),
            gates: buildGates(p, inferredTier),
            next_actions: [inferredTier.cta, "Complete auth/payment", "Receive scoped execution receipt"],
          },
        };
      }

      setSession({
        token: data.token || "local_session",
        sid: data.session?.sid || "local",
        expires_at: data.expires_at || new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
        intent: data.intent,
      });
      setNotice("Work packet staged. Activation required before execution.");

      // F7: Create a frontier build to start the mission packet
      if (clients) {
        try {
          const build: any = await clients.frontier.createBuild({
            source_url: "portal-intake",
            intent: p,
            target: tier !== "auto" ? tier : undefined,
            execution_mode: "DFY",
          });
          if (build?.build_id || build?.id) {
            setBuildData(build);
            // Poll build status
            setBuildPolling(true);
            const buildId = build.build_id || build.id;
            const poll = async () => {
              try {
                const status: any = await clients.frontier.getBuild(buildId);
                setBuildData(status);
                if (status.status === "pending" || status.status === "running") {
                  setTimeout(poll, 5000);
                } else {
                  setBuildPolling(false);
                }
              } catch {
                setBuildPolling(false);
              }
            };
            setTimeout(poll, 3000);
          }
        } catch {
          // Frontier worker may not be available — graceful failure
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [prompt, email, tier]);

  function reset() {
    setSession(null);
    setPrompt("");
    setEmail("");
    setTier("auto");
    setError(null);
    setNotice(null);
    setBuildData(null);
    setBuildPolling(false);
  }

  const intent = session?.intent;
  const recommendedTier = intent?.recommended_tier;
  const tiers = intent?.tiers || FALLBACK_TIERS;

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("Intake", membraneBypass)}</span>
        <h1>{applyMembrane("Work Packet", membraneBypass)}</h1>
      </div>

      {error && <div className="error-state">{error}</div>}
      {notice && <div className="card note-card">{applyMembrane(notice, membraneBypass)}</div>}

      {!session ? (
        <div className="work-packet-form card">
          <p className="card-meta" style={{ marginBottom: 16, lineHeight: 1.6 }}>
            {applyMembrane(
              "Type one request. The agent turns it into a scoped work packet. Execution stays locked until auth and payment are satisfied.",
              membraneBypass
            )}
          </p>

          <label className="field-label">{applyMembrane("Request / design brief", membraneBypass)}</label>
          <textarea
            className="chat-input"
            placeholder={applyMembrane(
              "Example: Build a cold outreach campaign for my AI consulting business targeting Series A startups.",
              membraneBypass
            )}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            maxLength={1800}
            style={{ marginBottom: 12 }}
          />

          <div className="work-packet-row">
            <div>
              <label className="field-label">{applyMembrane("Work email", membraneBypass)}</label>
              <input
                type="email"
                className="chat-input"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label className="field-label">{applyMembrane("Tier", membraneBypass)}</label>
              <select
                className="chat-input"
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="auto">auto-route</option>
                <option value="starter">$97/mo Starter</option>
                <option value="growth">$297/mo Growth</option>
                <option value="scale">$997/mo Scale</option>
                <option value="gtm">$10K GTM</option>
                <option value="board">Board Relationship</option>
              </select>
            </div>
          </div>

          <button
            className="btn-primary"
            onClick={createSession}
            disabled={busy}
            style={{ marginTop: 16 }}
          >
            {busy ? "Building…" : applyMembrane("Build my work packet", membraneBypass)}
          </button>

          <p className="muted" style={{ marginTop: 12, fontSize: "0.78rem" }}>
            {applyMembrane(
              "No email send, scrape, model action, charge, production deploy, or external workflow starts from this prompt.",
              membraneBypass
            )}
          </p>
        </div>
      ) : (
        <div className="work-packet-result">
          {/* Recommended tier */}
          {recommendedTier && (
            <div className="card">
              <div className="card-title">{applyMembrane("Recommended Tier", membraneBypass)}</div>
              <div className="tier-card active">
                <div className="tier-card-top">
                  <span className="tier-card-label">{applyMembrane(recommendedTier.label, membraneBypass)}</span>
                  <span className="tier-card-price">{recommendedTier.price}</span>
                </div>
                <div className="tier-card-gate muted">{applyMembrane(recommendedTier.gate, membraneBypass)}</div>
              </div>
            </div>
          )}

          {/* All tiers */}
          <div className="card">
            <div className="card-title">{applyMembrane("All Tiers", membraneBypass)}</div>
            <div className="tier-grid">
              {tiers.map((t) => {
                const active = t.id === recommendedTier?.id;
                return (
                  <div key={t.id} className={`tier-card ${active ? "active" : ""}`}>
                    <div className="tier-card-top">
                      <span className="tier-card-label">{applyMembrane(t.label, membraneBypass)}</span>
                      <span className="tier-card-price">{t.price}</span>
                    </div>
                    <div className="tier-card-gate muted">{applyMembrane(t.gate, membraneBypass)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Deliverables */}
          {intent?.deliverables && intent.deliverables.length > 0 && (
            <div className="card">
              <div className="card-title">{applyMembrane("Deliverables", membraneBypass)}</div>
              <ul className="deliverable-list">
                {intent.deliverables.map((d, i) => (
                  <li key={i} className="deliverable-item">{applyMembrane(d, membraneBypass)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Gates */}
          {intent?.gates && intent.gates.length > 0 && (
            <div className="card">
              <div className="card-title">{applyMembrane("Gates", membraneBypass)}</div>
              <ul className="gate-list">
                {intent.gates.map((g, i) => (
                  <li key={i} className="gate-item muted">{applyMembrane(g, membraneBypass)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Next actions */}
          {intent?.next_actions && intent.next_actions.length > 0 && (
            <div className="card">
              <div className="card-title">{applyMembrane("Next Actions", membraneBypass)}</div>
              <ol className="action-list">
                {intent.next_actions.map((a, i) => (
                  <li key={i} className="action-item">{applyMembrane(a, membraneBypass)}</li>
                ))}
              </ol>
            </div>
          )}

          {/* F7 Mission packet decomposition */}
          {buildData && (
            <div className="card">
              <div className="card-title">{applyMembrane("Mission Packet", membraneBypass)}</div>
              <div className="card-meta">
                <span className="badge" data-status={buildData.status === "completed" ? "active" : "pending"}>
                  {buildData.status || "pending"}
                </span>
                {buildData.build_id && <span className="mono muted"> {buildData.build_id.slice(0, 16)}</span>}
              </div>
              {buildData.work_units && Array.isArray(buildData.work_units) && (
                <div style={{ marginTop: 8 }}>
                  {buildData.work_units.map((unit: any, i: number) => (
                    <div key={i} className="card-list-item" style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: i > 0 ? "1px solid var(--border, #28313b)" : "none" }}>
                      <span className="mono muted" style={{ fontSize: "0.78rem" }}>→</span>
                      <span>{applyMembrane(unit.name || unit.title || `Unit ${i + 1}`, membraneBypass)}</span>
                      <span className="badge" data-status={unit.status === "done" ? "active" : "pending"}>{unit.status || "pending"}</span>
                      {unit.depends_on && unit.depends_on.length > 0 && (
                        <span className="muted" style={{ fontSize: "0.72rem" }}>depends: {unit.depends_on.join(", ")}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {buildPolling && <p className="muted" style={{ marginTop: 6, fontSize: "0.78rem" }}>Polling for updates…</p>}
            </div>
          )}

          <div className="work-packet-actions">
            <button className="btn-secondary" onClick={reset}>
              {applyMembrane("New work packet", membraneBypass)}
            </button>
            <span className="muted" style={{ fontSize: "0.78rem" }}>
              {applyMembrane(`Session: ${session.sid.slice(0, 12)}…`, membraneBypass)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
